import React, { useEffect, useState } from 'react';
import { X, Loader2, Copy, Mail, Sparkles, Building2, Car, Wrench } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const money = (n) => `€${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Kpi = ({ label, value, testid }) => (
  <div className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{label}</div>
    <div className="text-base font-extrabold text-slate-800 tabular-nums mt-0.5">{value}</div>
  </div>
);

/** Right-side client card opened from the Analytics → Clients table. */
export default function AnalyticsClientCard({ token, userId, range, customStart, customEnd, onClose, onStep }) {
  const [scope, setScope] = useState('period');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAllOrders, setShowAllOrders] = useState(false);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowDown') { e.preventDefault(); onStep?.(1); }
      if (e.key === 'ArrowUp')   { e.preventDefault(); onStep?.(-1); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, onStep]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      const p = new URLSearchParams({ range, scope });
      if (range === 'custom') { p.set('start', customStart); p.set('end', customEnd); }
      try {
        const r = await fetch(`${API_URL}/api/admin/analytics/clients/${userId}?${p.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const j = r.ok ? await r.json() : null;
        if (alive) setData(j);
      } catch (_) { if (alive) setData(null); }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [userId, range, scope, customStart, customEnd, token]);

  const p = data?.profile || {};
  const k = data?.kpis || {};
  const orders = data?.orders || [];
  const shown = showAllOrders ? orders : orders.slice(0, 5);

  const copyEmail = () => {
    navigator.clipboard?.writeText(p.email || '');
    toast.success('Copied');
  };

  return (
    <div className="fixed inset-0 z-[70] flex justify-end" data-testid="an-client-card">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full sm:w-[560px] h-full bg-white shadow-2xl overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-gradient-to-r from-slate-800 to-slate-900 text-white px-4 py-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <h3 className="font-bold text-base truncate flex items-center gap-2">
                {loading ? 'Loading…' : p.name}
                {p.is_new && <span title="New client" className="w-4 h-4 rounded-full bg-emerald-500 inline-flex items-center justify-center"><Sparkles className="w-2.5 h-2.5" /></span>}
                {p.is_company && <span title={`Company account${p.company_name ? ` · ${p.company_name}` : ''}`} className="w-4 h-4 rounded-full bg-blue-500 inline-flex items-center justify-center"><Building2 className="w-2.5 h-2.5" /></span>}
              </h3>
              <div className="text-[11px] text-white/70 truncate flex items-center gap-2 mt-0.5">
                <button onClick={copyEmail} data-testid="an-client-copy-email" className="hover:text-white inline-flex items-center gap-1">
                  {p.email} <Copy className="w-3 h-3" />
                </button>
                {p.account_no ? <span>· #{p.account_no}</span> : null}
              </div>
              <div className="text-[11px] text-white/60 mt-0.5">
                Registered {p.registered_at || '—'}{p.country ? ` · ${p.country}` : ''}
              </div>
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              {p.email && (
                <a href={`mailto:${p.email}`} data-testid="an-client-mailto" title="Email customer" className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20">
                  <Mail className="w-4 h-4" />
                </a>
              )}
              <button onClick={onClose} data-testid="an-client-close" className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
          {/* Period / All time */}
          <div className="flex items-center gap-1 mt-2.5">
            {[{ id: 'period', label: data?.period?.label || 'Period' }, { id: 'all_time', label: 'All time' }].map((o) => (
              <button
                key={o.id}
                onClick={() => { setScope(o.id); setShowAllOrders(false); }}
                data-testid={`an-client-scope-${o.id}`}
                className={`px-2.5 py-1 rounded-full text-[11px] font-bold ${scope === o.id ? 'bg-white text-slate-900' : 'bg-white/10 text-white/80 hover:bg-white/20'}`}
              >{o.label}</button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="py-20 flex items-center justify-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin" /></div>
        ) : !data ? (
          <div className="py-20 text-center text-slate-400 text-sm">Could not load this client.</div>
        ) : (
          <div className="p-4 space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <Kpi label="Orders" value={k.orders ?? 0} testid="an-client-kpi-orders" />
              <Kpi label="Ordered" value={money(k.amount)} testid="an-client-kpi-amount" />
              <Kpi label="Avg check" value={money(k.avg_check)} testid="an-client-kpi-avg" />
              <Kpi label="Credit balance" value={money(k.credit_balance)} testid="an-client-kpi-balance" />
            </div>
            <p className="text-xs text-slate-500">
              Last order: <b>{k.last_order_at || '—'}</b>
              {k.days_since_last_order != null && ` · ${k.days_since_last_order} days ago`}
              {k.reserved ? ` · reserved ${money(k.reserved)}` : ''}
            </p>

            {(data.top_vehicles?.length > 0 || data.top_services?.length > 0) && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="border border-slate-200 rounded-lg p-3">
                  <div className="text-[11px] uppercase font-bold text-slate-500 flex items-center gap-1.5 mb-2"><Car className="w-3.5 h-3.5" /> Top vehicles</div>
                  {(data.top_vehicles || []).map((v) => (
                    <div key={v.label} className="flex justify-between text-xs py-0.5">
                      <span className="truncate text-slate-700">{v.label}</span>
                      <span className="font-mono text-slate-500">×{v.count}</span>
                    </div>
                  ))}
                </div>
                <div className="border border-slate-200 rounded-lg p-3">
                  <div className="text-[11px] uppercase font-bold text-slate-500 flex items-center gap-1.5 mb-2"><Wrench className="w-3.5 h-3.5" /> Top services</div>
                  {(data.top_services || []).map((v) => (
                    <div key={v.label} className="flex justify-between text-xs py-0.5">
                      <span className="truncate text-slate-700">{v.label}</span>
                      <span className="font-mono text-slate-500">×{v.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <div className="text-[11px] uppercase font-bold text-slate-500 mb-2">Orders ({data.orders_total})</div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="text-slate-500">
                    <tr>
                      <th className="text-left py-1.5 px-1.5 font-bold">Order #</th>
                      <th className="text-left py-1.5 px-1.5 font-bold">Date</th>
                      <th className="text-left py-1.5 px-1.5 font-bold">Vehicle</th>
                      <th className="text-right py-1.5 px-1.5 font-bold">Amount</th>
                      <th className="text-left py-1.5 px-1.5 font-bold">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map((o) => (
                      <tr key={o.order_no} className="border-t border-slate-100" data-testid={`an-client-order-${o.order_no}`}>
                        <td className="py-1.5 px-1.5 font-mono font-bold text-slate-700 whitespace-nowrap">{o.order_no}</td>
                        <td className="py-1.5 px-1.5 whitespace-nowrap text-slate-500">{o.date}</td>
                        <td className="py-1.5 px-1.5 max-w-[160px] truncate" title={`${o.vehicle} · ${o.ecu_type || ''} · ${(o.services || []).join(', ')}`}>{o.vehicle}</td>
                        <td className="py-1.5 px-1.5 text-right font-mono tabular-nums">{money(o.amount)}</td>
                        <td className="py-1.5 px-1.5 text-slate-500">{o.status}</td>
                      </tr>
                    ))}
                    {shown.length === 0 && (
                      <tr><td colSpan={5} className="py-4 text-center text-slate-400">No orders in this scope.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              {orders.length > 5 && !showAllOrders && (
                <button onClick={() => setShowAllOrders(true)} data-testid="an-client-orders-all" className="mt-2 text-xs font-bold text-cyan-700 hover:underline">
                  Show all {orders.length}
                </button>
              )}
            </div>

            {(data.recharges || []).length > 0 && (
              <div>
                <div className="text-[11px] uppercase font-bold text-slate-500 mb-2">Recharges</div>
                <table className="w-full text-xs">
                  <tbody>
                    {data.recharges.slice(0, 5).map((t, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="py-1.5 px-1.5 text-slate-500 whitespace-nowrap">{t.date}</td>
                        <td className="py-1.5 px-1.5 text-right font-mono tabular-nums">{money(t.amount)}</td>
                        <td className="py-1.5 px-1.5 text-slate-500 capitalize">{t.method}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
